import { type RefObject, useMemo } from "react";
import { useElementDimensions } from "@/hooks/use-element-dimensions";

export interface NearWindowEdges {
    top: boolean;
    bottom: boolean;
    left: boolean;
    right: boolean;
}

export interface UseEdgeProximityOptions {
    /** Optional: External ref to measure (if you already have a ref) */
    ref?: RefObject<HTMLElement | null>;
    /** Distance from edge in pixels to be considered "near" */
    threshold: number;
}

export interface UseEdgeProximityReturn {
    /** Whether element is near any edge */
    isNearWindowEdge: boolean;
    /** Detailed edge proximity information */
    nearWindowEdges: NearWindowEdges;
    /** Ref to attach to element (if not using external ref) */
    ref?: RefObject<HTMLElement | null>;
}

/**
 * Hook for detecting if an element is near the window edge.
 *
 * Useful for:
 * - Positioning dropdowns/menus to avoid overflow
 * - Adjusting tooltip placement
 * - Responsive UI adjustments based on element position
 *
 * Supports external refs as well as internal ref creation.
 * @param options - Configuration including optional ref and threshold distance
 * @returns Edge detection results and ref
 */
export const useEdgeProximity = ({ ref: externalRef, threshold }: UseEdgeProximityOptions): UseEdgeProximityReturn => {
    const { position, ref } = useElementDimensions({
        ref: externalRef,
        respondToMapMovement: true,
    });

    const result = useMemo((): { isNearWindowEdge: boolean; nearWindowEdges: NearWindowEdges } => {
        if (!position) {
            return {
                isNearWindowEdge: false,
                nearWindowEdges: { top: false, bottom: false, left: false, right: false },
            };
        }

        const nearWindowEdges: NearWindowEdges = {
            top: position.top < threshold,
            bottom: position.bottom > window.innerHeight - threshold,
            left: position.left < threshold,
            right: position.right > window.innerWidth - threshold,
        };

        return {
            isNearWindowEdge: Object.values(nearWindowEdges).some(Boolean),
            nearWindowEdges,
        };
    }, [position, threshold]);

    return { ...result, ref };
};
